import "katex/dist/katex.min.css";

import type { FC } from "react";
import { useEffect, useState } from "react";
import { ButtonGroup, ToggleButton } from "react-bootstrap";
import Latex from "react-latex-next";
import { useLocation } from "react-router-dom";

import { ExerciseFooter } from "./components/ExerciseFooter";
import { useAnswerCheck } from "./useAnswerCheck";

import { AbilityScore, AnswerExercise, ErrorModal } from "components";
import AnswerVideo from "components/Video/AnswerVideo";
import { ExerciseSelfEvaluation } from "features";

import type { UserAssignmentForExerciseFragment } from "api/generated";

import { evaluateSorting, getSolutionVariant } from "utils/getSolutionVariant";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getAssignmentResult,
  setAbilityScore,
  setAnswer,
  setAssignmentEvaluation,
  setTimeLearn,
  setTips,
} from "store/slices/exercise";
import { getAssignmentTime, resetAssignmentTimer, startAssignmentTimer, stopAssignmentTimer } from "store/slices/timer";

import { AssignmentPrompt } from "components/AssignmentPrompt";
import { SuccessRate } from "components/SuccessRate/SuccessRate";
import { changeDistinctAbilityScore, getAbilityScoreOptions } from "utils/changeAbilityScore";
import { getTimeToLearn } from "utils/getTimeToLearn";
import { useEmptyAssignmentAnswer } from "utils/hooks/useEmptyAssignmentAnswer";
import { useSaveExerciseSelfEvaluation } from "utils/hooks/useSaveExerciseSelfEvaluation";
import { isAbilityScoreShown } from "utils/isAbilityScoreShown";
import { logGeneralExerciseError } from "utils/processApiError";
import { isSelfEvaluationForEveryAssignment } from "utils/valueTranslators";

/* rPp7XgOY9DxQXaSbZsBQ */

interface Props {
  data: UserAssignmentForExerciseFragment;
  userAssignmentId: string;
  evaluateAnswer: boolean;
  onNextClick: () => void;
}

const Razeni: FC<Props> = ({ data, userAssignmentId, evaluateAnswer, onNextClick }) => {
  const questionsCount = data.assignment?.assignment?.questions.length ?? 0;

  const options =
    data.assignment?.assignment?.questions.map(question => question.options.map(option => option.value)) ?? [];
  const isSelfEvaluation = data.assignment?.scoring?.scoringMethod?.scoringMethod === "sebeopravení";

  const saveExerciseSelfEvaluation = useSaveExerciseSelfEvaluation();
  const dispatch = useAppDispatch();
  const assignmentResults = useAppSelector(getAssignmentResult<string>(userAssignmentId));
  const time = useAppSelector(getAssignmentTime);
  const { setEmptyExerciseAnswer } = useEmptyAssignmentAnswer();

  const { correctAnswers, points, check, isChecking, isErrorModalVisible, hideErrorModal } = useAnswerCheck({
    data,
    userAssignmentId,
    evaluateAnswer,
  });

  const [goNext, setGoNext] = useState(false);

  const location = useLocation();
  const isOnboarding = location.pathname.includes("onboarding");

  useEffect(() => {
    dispatch(resetAssignmentTimer());

    if (assignmentResults?.answers === undefined || assignmentResults.answers.length === 0) {
      const defaultValue = new Array(data.assignment?.assignment?.questions.length).fill(" ".repeat(options[0].length));
      dispatch(setAnswer({ assignmentId: userAssignmentId, answer: defaultValue }));
    }

    if (assignmentResults?.tips === undefined || assignmentResults.tips.length === 0) {
      dispatch(setTips({ userAssignmentId, tips: new Array(questionsCount).fill(false) }));
    }

    dispatch(startAssignmentTimer());

    return () => {
      dispatch(resetAssignmentTimer());
    };
  }, []);

  useEffect(() => {
    if (correctAnswers === undefined) return;
    const responseAnswers = correctAnswers.map(questionAnswers =>
      questionAnswers
        .filter((answer): answer is string => answer !== null)[0]
        ?.split("")
        .map(value =>
          data.assignment?.assignment?.isAnswerCaseSensitive === true ? [value] : [value.toLocaleLowerCase()],
        ),
    );

    if (assignmentResults?.answers === undefined) return;

    dispatch(
      setAssignmentEvaluation({
        assignmentId: userAssignmentId,
        status: evaluateSorting(assignmentResults.answers[0]?.split(""), responseAnswers[0]),
        answerVariants: evaluateAnswers(responseAnswers[0]),
        correctAnswers: responseAnswers.flat(),
      }),
    );
  }, [correctAnswers]);

  useEffect(() => {
    if (goNext) {
      dispatch(setTimeLearn({ assignmentId: userAssignmentId, timeLearn: time }));
      onNextClick();
      setGoNext(false);
    }
  }, [goNext]);

  const evaluateAnswers = (answers: Array<Array<string | null>>) => {
    if (data.assignment?.assignment === undefined || data.assignment?.assignment === null) return [];

    if (assignmentResults?.answers === undefined) return [];

    const selectedChars = assignmentResults.answers[0]?.split("");
    return data.assignment.assignment.questions[0]?.options.map((_option, index) =>
      getSolutionVariant(
        selectedChars[index],
        answers,
        index,
        data.assignment?.assignment?.isAnswerCaseSensitive ?? false,
      ),
    );
  };

  const handleCheck = (index: number, position: number, newValue: string | null | undefined) => {
    if (typeof newValue === "string") {
      if (assignmentResults?.answers === undefined) return;

      const copy = [...assignmentResults.answers];
      const currentQuestion = copy[index].split("");
      currentQuestion[position] = newValue;
      copy[index] = currentQuestion.join("");

      dispatch(setAnswer({ assignmentId: userAssignmentId, answer: copy }));
    }
  };

  const onSubmit = () => {
    check(assignmentResults?.answers?.map(answer => ({ answers: [answer ?? ""] })));
  };

  const handleNextClick = () => {
    dispatch(stopAssignmentTimer());
    setGoNext(true);
  };

  const areAnswersFilled = () => {
    try {
      const answers = assignmentResults?.answers;
      const areFilled =
        answers !== undefined &&
        answers.length > 0 &&
        answers.every(answer => answer.split("").every(char => char !== " "));
      return areFilled;
    } catch (error) {
      logGeneralExerciseError(error, data?.assignment?.id, assignmentResults?.answers);
      return false;
    }
  };

  const handleAbilityScoreSelect = (value: number, index: number) => {
    if (
      data.assignment === undefined ||
      data.assignment === null ||
      !isSelfEvaluationForEveryAssignment(data.assignment)
    )
      return;

    const newAbilityScore = changeDistinctAbilityScore(value, index, assignmentResults?.abilityScore, questionsCount);
    dispatch(setAbilityScore({ assignmentId: userAssignmentId, score: newAbilityScore }));
  };

  const getAbilityScoreOnIndex = (index: number) => {
    const abilityScore = assignmentResults?.abilityScore;

    if (abilityScore === undefined || typeof abilityScore === "number") return 0;

    return abilityScore[index];
  };

  const analysis =
    data.assignment?.analysis?.hasSharedAnalysis === true
      ? data.assignment?.analysis.sharedAnalysis
      : data.assignment?.assignment?.questions.at(0)?.solutionAnalysis;

  const isIncorrect = assignmentResults?.answersVariants?.includes("danger");

  if (isSelfEvaluation) {
    if (data.assignment === undefined || data.assignment === null) return null;

    return (
      <ExerciseSelfEvaluation
        userAssignment={data}
        userAssignmentId={userAssignmentId}
        results={assignmentResults}
        onNextClick={handleNextClick}
        saveAnswer={saveExerciseSelfEvaluation}
        evaluateAnswer={evaluateAnswer}
      />
    );
  }

  return (
    <div className="exercise__body">
      <AssignmentPrompt assignment={data.assignment?.assignment} />

      <div>
        {data.assignment?.assignment?.questions.map((question, qIndex) => (
          <div key={`wrapper-${qIndex}`}>
            <Latex>{question.heading ?? ""}</Latex>

            {question.options.map((option, oIndex) => (
              <div
                key={`text-${qIndex}-${oIndex}`}
                className="assignment-option"
                dangerouslySetInnerHTML={{ __html: option.additionalText ?? "" }}
              ></div>
            ))}
            <div className="question question--list mt-20px">
              {options[qIndex].map((_values, vIndex) => {
                if (assignmentResults?.correctAnswers === undefined) {
                  return (
                    <div key={`question-${qIndex}-${vIndex}`} className="button-group">
                      {assignmentResults?.answers !== undefined && assignmentResults?.answers?.length > 0 ? (
                        <>
                          <span>{`${vIndex + 1}.`}</span>
                          <ButtonGroup className={"btn-group-select btn-shadow"}>
                            {options[qIndex].map((option, rIndex) => {
                              const currentValue = assignmentResults?.answers?.[qIndex][vIndex] ?? "";
                              return (
                                <ToggleButton
                                  key={rIndex}
                                  id={`radio-${qIndex}-${vIndex}-${rIndex}`}
                                  type="radio"
                                  variant="select"
                                  value={option ?? ""}
                                  checked={currentValue === option}
                                  onChange={() => {
                                    handleCheck(qIndex, vIndex, option);
                                  }}
                                >
                                  {option}
                                </ToggleButton>
                              );
                            })}
                          </ButtonGroup>
                        </>
                      ) : null}
                    </div>
                  );
                }

                if (assignmentResults?.answers === undefined) return null;

                const selectedString = qIndex in assignmentResults.answers ? assignmentResults?.answers[qIndex] : "";
                const selfEvalOptions = getAbilityScoreOptions(
                  isSelfEvaluation,
                  assignmentResults?.answersVariants?.[qIndex],
                );

                return (
                  <>
                    <div key={`answer-${vIndex}`} className="button-group">
                      <span>{`${vIndex + 1}.`}</span>
                      <AnswerExercise
                        subject={data.subject?.appName}
                        key={`answer-${qIndex}`}
                        index={qIndex}
                        assignment={data.assignment}
                        userAssignmentId={data.id}
                        type={assignmentResults.answersVariants?.[vIndex] ?? "danger"}
                        timeToLearn={getTimeToLearn(data.timeToLearn)}
                        onNext={handleNextClick}
                        hideVideo={true}
                      >
                        {selectedString[vIndex]}
                      </AnswerExercise>
                    </div>

                    {data.assignment !== undefined &&
                    data.assignment !== null &&
                    isSelfEvaluationForEveryAssignment(data.assignment) &&
                    question.showAbilityScore === true &&
                    selfEvalOptions !== null ? (
                      <>
                        <hr className={"my-25px"} />
                        <div className="mw-sm">
                          <AbilityScore
                            isSelfEvaluated={isSelfEvaluation}
                            options={selfEvalOptions}
                            selectedValue={getAbilityScoreOnIndex(qIndex)}
                            onValueSelect={(value: number) => {
                              handleAbilityScoreSelect(value, qIndex);
                            }}
                            index={qIndex}
                            key={qIndex}
                          />
                        </div>
                      </>
                    ) : null}
                  </>
                );
              })}

              {isOnboarding ? null : (
                <AnswerVideo videoId={analysis?.explainerVideo} answerType={isIncorrect ? "danger" : "success"} />
              )}

              {typeof data.assignment?.successRate === "number" ? (
                <SuccessRate successRate={data.assignment?.successRate} />
              ) : null}
            </div>
          </div>
        ))}

        <ErrorModal
          onCancel={() => {
            setEmptyExerciseAnswer(userAssignmentId, evaluateAnswers);
            hideErrorModal();
          }}
          onHide={() => {
            hideErrorModal();
          }}
          onRetry={() => {
            onSubmit();
          }}
          show={isErrorModalVisible}
        />

        <ExerciseFooter
          onSubmit={onSubmit}
          isAnalysis={assignmentResults?.correctAnswers !== undefined}
          goNext={handleNextClick}
          isSubmitDisabled={!areAnswersFilled() || isChecking}
          userAssignmentId={userAssignmentId}
          showAbilityScore={isAbilityScoreShown(data.assignment?.scoring?.selfEvaluation?.selfEvaluation)}
          points={points}
          maxPoints={points === null ? null : 1}
        />
      </div>
    </div>
  );
};

export default Razeni;
